import { useTranslation } from 'react-i18next';

import Growth from '@/assets/icons/growth.svg?component';
import LivingWeight from '@/assets/icons/living_weight.svg?component';
import Tooltip from '@/components/atoms/Tooltip';
import ContentBlock from '@/components/molecules/ContentBlock';
import { MetricCard } from '@/components/molecules/MetricCard';
import type { DomainCageOverviewPeriod } from '@/domain/cages/overview.types';
import WeightAndGrowthSummary from '@/routes/Client/Cage/components/WeightAndGrowthSummary';
import GridLayout from '@/routes/Client/Cage/pages/Reports/shared/GridLayout';
import { formatNumberByLocale } from '@/utils/localeFormatters';

import ViewButton from './ViewButton';

import styles from './cardParts.module.scss';

const GROWTH_DECIMALS = 1;
const NO_VALUE = '-';

interface WeightAndGrowthCardProps {
    /** Living weight on the last measurement day, in `weightUnitTag`. */
    estimatedWeight: number | null;
    period: DomainCageOverviewPeriod;
    weightUnitTag: string;
    isLoading: boolean;
    weightUrl: string;
}

/** Living weight and growth up top, the period ratios below, as on the weight dashboard. */
export default function WeightAndGrowthCard(props: WeightAndGrowthCardProps) {
    const { estimatedWeight, period, weightUnitTag, isLoading, weightUrl } = props;
    const { t, i18n } = useTranslation();

    // A zero estimate is the backend's way of saying there is none.
    const hasWeight = estimatedWeight != null && estimatedWeight !== 0;
    const growth = formatNumberByLocale(
        period.weightGrowth,
        i18n.language,
        GROWTH_DECIMALS,
        NO_VALUE
    );

    return (
        <ContentBlock
            title={t('common.labels.weightAndGrowth')}
            spacing="none"
            headerActions={<ViewButton to={weightUrl} />}>
            <GridLayout contentLayout="2" className={styles.metricsRow}>
                <Tooltip content={t('common.labels.estimatedLivingWeight')}>
                    <MetricCard
                        title={t('common.labels.livingWeight')}
                        value={
                            hasWeight
                                ? formatNumberByLocale(estimatedWeight, i18n.language, 0)
                                : NO_VALUE
                        }
                        suffix={hasWeight ? weightUnitTag : undefined}
                        icon={LivingWeight}
                        tone="caution"
                        iconCustomSize={22}
                        isLoading={isLoading}
                        framed="tinted"
                    />
                </Tooltip>
                <Tooltip content={t('descriptions.weightAndGrowthGrowth')}>
                    <MetricCard
                        title={t('common.labels.growth')}
                        value={growth}
                        suffix={
                            growth === NO_VALUE
                                ? undefined
                                : `${t(`weightUnitTag.${weightUnitTag}`)}${t('common.labels.perDay')}`
                        }
                        icon={Growth}
                        tone="info"
                        iconCustomSize={20}
                        isLoading={isLoading}
                        framed="tinted"
                    />
                </Tooltip>
            </GridLayout>
            <WeightAndGrowthSummary
                layout="row"
                period={period}
                weightUnitTag={weightUnitTag}
                isLoading={isLoading}
            />
        </ContentBlock>
    );
}
